import merge from 'lodash.merge';
import hasOwnProperty from '../hasOwnProperty';

/**
 * @callback PropSelectorCallback - a callback called for each prop passed to a component
 * @param {string} key - the key for the prop to be tested
 * @param {*} value - the value of the prop being passed in to the component
 * @returns {object|undefined}
 */

/**
 * @param {PropSelectorCallback} callback
 * @param {object} items
 * @param {string} key
 * @param {*} value
 * @returns {object|undefined}
 */
const applyCallback = (callback, items, key, value) => {
  // If there's no callback, continue and look up keys as normal
  if (typeof callback !== 'function') return undefined;
  const newItems = callback(key, value);

  // If the callback doesn't return anything, continue and look up keys as normal
  if (!newItems) return undefined;

  // If the callback returns items, merge them in, deep merging props that are objects
  return merge({}, items, newItems);
};

/**
 * Generates a function to filter an object of props down to a subset of allowed props, with
 * optional prop alteration and re-mapping via an optional callback.
 *
 * @param {object} propTypes - an object where every defined key is a valid prop
 * @param {*} [regexp] - an optional regular expression where any match is a valid prop
 * @param {PropSelectorCallback} callback - optional function taking `(key, value)` returning either undefined or an object of new props to merge in
 * @returns {object} - valid props for this component
 */
export default function getPropSelector(propTypes, regexp, callback) {
  return props => Object.entries(props).reduce((items, _ref) => {
    let [key, value] = _ref;
    return (
      // If there's a callback and it matches something, applyCallback merges it in; return that
      applyCallback(callback, items, key, value) || (
      // If there's no callback match, check if this prop is valid and merge it in if it is
      hasOwnProperty(propTypes, key) || regexp && regexp.test(key) ? {
        ...items,
        [key]: value
      } : items)
    );
  }, {});
}